import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { StyleSheet, View, Platform, } from 'react-native';
import Animated, { interpolate, Extrapolate, useAnimatedScrollHandler, useAnimatedStyle, useDerivedValue, useSharedValue, } from 'react-native-reanimated';
import { useToken } from 'native-base';
export const DEFAULT_PICKER_ITEM_HEIGHT = 48;
export const DEFAULT_PICKER_VISIBLE_ITEMS = 5;
const styles = StyleSheet.create({
    selectedView: {
        alignItems: 'center',
        justifyContent: 'center',
    },
    viewArea: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
    },
    contentContainer: {
    // no styles
    },
    flatList: {
        flexGrow: 0,
    },
    label: {
        flex: 1,
    },
    itemView: {
        alignItems: 'center',
        justifyContent: 'center',
    },
    itemText: {
        textAlign: 'center',
        textAlignVertical: 'center',
        width: '100%',
    },
});
export const ADCPickerItem = ({ currentIndex, height, index, item, scrollOffsetY, scrollPickerHeight, testID = '', withStyles, }) => {
    const itemColor = useToken('colors', 'neutral.50');
    const itemFontSize = useToken('fontSizes', '2xl');
    // we can't do useToken('fontWeights', 'regular') because the font weight
    // tokens are not valid React Native values
    const itemFontWeight = '400';
    const itemSelectedColor = useToken('colors', 'text.100');
    const itemSelectedFontWeight = '600';
    const minOffsetRange = useMemo(() => index * height + height / 2 - scrollPickerHeight / 2, [height, index, scrollPickerHeight]);
    const maxOffsetRange = useMemo(() => index * height + height / 2 + scrollPickerHeight / 2, [height, index, scrollPickerHeight]);
    // animations
    const progressY = useDerivedValue(() => {
        return interpolate(
        // interpolates current items distance from the center of the view
        // in a range between -1 to 1
        scrollOffsetY?.value + height / 2, [minOffsetRange, maxOffsetRange], [-1, 1], Extrapolate.CLAMP);
    }, [scrollOffsetY]);
    const animatedViewStyles = useAnimatedStyle(() => {
        return {
            opacity: 1 - Math.abs(progressY?.value),
            transform: [
                { translateY: (progressY?.value * height) / 2 },
                {
                    scale: 1 - 0.3 * Math.abs(progressY?.value),
                },
            ],
        };
    }, [progressY]);
    const animatedTextStyles = useAnimatedStyle(() => {
        const inRange = currentIndex.value === index;
        return {
            color: inRange ? itemSelectedColor : itemColor,
            fontWeight: inRange ? itemSelectedFontWeight : itemFontWeight,
            letterSpacing: inRange ? 0 : 1, // prevents bold text from changing width of content
        };
    }, [itemColor, itemFontWeight, itemSelectedColor, itemSelectedFontWeight, progressY]);
    return (React.createElement(Animated.View, { testID: `${testID}-view`, accessibilityState: {
            selected: currentIndex.value === index,
        }, style: [
            styles.itemView,
            {
                height,
            },
            animatedViewStyles,
            withStyles,
        ] },
        React.createElement(Animated.Text, { testID: `${testID}-text`, style: [
                styles.itemText,
                {
                    fontSize: +itemFontSize,
                },
                animatedTextStyles,
            ] }, item.label)));
};
export const ADCPicker = ({ containerStyles, disabled = false, flatListContainerStyles, flatListStyles, height = DEFAULT_PICKER_ITEM_HEIGHT, items, itemStyles, leftLabel, hideLabels = false, onChange, rightLabel, selectedIndex, selectedOutline, testID = '', visibleItems = DEFAULT_PICKER_VISIBLE_ITEMS, }) => {
    // design tokens
    const labelSpacing = useToken('sizes', 2);
    const ITEM_OFFSET = height / 2; // needed to calculate center of items
    const currentIndex = useSharedValue(selectedIndex ?? 0);
    const scrollOffsetY = useSharedValue(0);
    const visibleScrollPickerHeight = useRef(height * visibleItems);
    const flatListRef = useRef(null);
    // resolves issue on Android where onMomentumScrollEnd fires multiple times
    // cf. https://github.com/facebook/react-native/issues/32696#issuecomment-1104217223
    const canMomentum = useRef(false);
    const onMomentumScrollBegin = useCallback(() => {
        canMomentum.current = true;
    }, [canMomentum]);
    const onMomentumScrollEnd = useCallback(({ nativeEvent }) => {
        if (canMomentum.current === true && onChange != null && typeof onChange === 'function') {
            const index = Math.floor((nativeEvent.contentOffset.y + ITEM_OFFSET) / height);
            currentIndex.value = index;
            const item = items?.[index];
            if (item == null || selectedIndex === index) {
                return;
            }
            onChange(item.value, index);
        }
        canMomentum.current = false;
    }, [canMomentum, currentIndex, height, ITEM_OFFSET, items, onChange, selectedIndex]);
    const onScroll = useAnimatedScrollHandler((nativeEvent) => {
        scrollOffsetY.value = nativeEvent.contentOffset.y;
        // get index of item that is in center of selected view
        const index = Math.floor((nativeEvent.contentOffset.y + ITEM_OFFSET) / height);
        currentIndex.value = index;
    });
    // FlatList works differently in react-native-web, so these functions act
    // as a polyfill to emulate the same scrolling behaviour as on native
    const onScrollEndWeb = (event) => {
        if (onChange != null && typeof onChange === 'function') {
            const index = Math.floor((event.nativeEvent.contentOffset.y + ITEM_OFFSET) / height);
            currentIndex.value = index;
            const item = items?.[index];
            if (item == null || selectedIndex === index) {
                return;
            }
            onChange(item.value, index);
        }
    };
    const onScrollWeb = (event) => {
        scrollOffsetY.value = event.nativeEvent.contentOffset.y;
    };
    const handleWebScroll = useWebOnScroll({ onScroll: onScrollWeb, onScrollEnd: onScrollEndWeb });
    // handle controlled input from user changing selectedIndex
    useEffect(() => {
        if (flatListRef.current != null &&
            selectedIndex != null &&
            selectedIndex !== currentIndex.value &&
            selectedIndex >= 0 &&
            selectedIndex < items.length &&
            items?.length > 0) {
            // the below typecast is needed to circumvent Animated.FlatList having
            // the incorrect typings for FlatList ref methods
            // cf. https://github.com/software-mansion/react-native-reanimated/issues/587
            flatListRef.current?.scrollToIndex({
                index: selectedIndex,
            });
        }
    }, [currentIndex, items, selectedIndex]);
    const renderItem = useCallback(({ item, index }) => {
        return (React.createElement(ADCPickerItem, { testID: `${testID}-picker-item-${index}`, currentIndex: currentIndex, height: height, index: index, item: item, scrollOffsetY: scrollOffsetY, scrollPickerHeight: visibleScrollPickerHeight.current, withStyles: itemStyles }));
    }, [currentIndex, height, itemStyles, scrollOffsetY, testID, visibleScrollPickerHeight]);
    const getItemLayout = React.useCallback((data, index) => ({
        length: height,
        offset: height * index,
        index,
    }), [height]);
    return (React.createElement(View, { testID: `${testID}-picker`, style: [
            styles.viewArea,
            {
                height: height * visibleItems,
                paddingHorizontal: labelSpacing,
            },
            containerStyles,
        ] },
        selectedOutline && (React.createElement(View, { testID: `${testID}-picker-selected-outline`, style: [StyleSheet.absoluteFill, styles.selectedView] }, selectedOutline)),
        !hideLabels && (React.createElement(View, { testID: `${testID}-picker-left-label`, style: [styles.label, { marginHorizontal: labelSpacing }] }, leftLabel)),
        React.createElement(Animated.FlatList, { accessibilityRole: "adjustable", accessibilityValue: {
                text: `${items?.[selectedIndex ?? -1]?.value}`,
            }, testID: `${testID}-picker-flat-list`, style: [styles.flatList, flatListStyles], contentContainerStyle: [
                styles.contentContainer,
                Platform.OS === 'web'
                    ? {
                        height: height * visibleItems,
                    }
                    : undefined,
                {
                    paddingVertical: height * Math.floor(visibleItems / 2),
                    paddingHorizontal: labelSpacing,
                },
                flatListContainerStyles,
            ], ref: flatListRef, data: items, showsVerticalScrollIndicator: false, scrollEnabled: !disabled, snapToInterval: height, initialScrollIndex: selectedIndex, onMomentumScrollBegin: onMomentumScrollBegin, onMomentumScrollEnd: onMomentumScrollEnd, 
            // optimizations
            // cf. https://reactnative.dev/docs/optimizing-flatlist-configuration
            renderItem: renderItem, keyExtractor: (item) => `${item.value}`, getItemLayout: getItemLayout, scrollEventThrottle: 16, maxToRenderPerBatch: 30, updateCellsBatchingPeriod: 25, windowSize: 61, 
            // web
            disableScrollViewPanResponder: true, pagingEnabled: true, onScroll: Platform.select({ web: handleWebScroll, default: onScroll }) }),
        !hideLabels && (React.createElement(View, { testID: `${testID}-picker-right-label`, style: [styles.label, { marginHorizontal: labelSpacing }] }, rightLabel))));
};
/**
 * This hook is needed to determine when the end of a scroll event happens
 * on web.
 *
 * cf. https://github.com/necolas/react-native-web/issues/1021#issuecomment-984151185
 * @param props
 * @returns
 */
export const useWebOnScroll = ({ onScroll, onScrollEnd }) => {
    const lastScrollEvent = useRef();
    const scrollEndTimeout = useRef();
    const handleWebScroll = (event) => {
        onScroll(event);
        const timestamp = Date.now();
        if (scrollEndTimeout.current) {
            clearTimeout(scrollEndTimeout.current);
        }
        if (lastScrollEvent.current) {
            // Scroll ended
            scrollEndTimeout.current = setTimeout(() => {
                if (lastScrollEvent.current === timestamp) {
                    lastScrollEvent.current = undefined;
                    onScrollEnd && onScrollEnd(event);
                }
            }, 500);
        }
        lastScrollEvent.current = timestamp;
    };
    useEffect(() => {
        return () => {
            scrollEndTimeout.current && clearTimeout(scrollEndTimeout.current);
        };
    }, []);
    return handleWebScroll;
};
